import { FRONTEND_API_ENDPOINTS } from "@/config/apiEndpoints/frontendUrls";
import { APP_URL } from "@/config/urls";
/** Use sectionTypes only — importing `@/constants/admin` pulls SECTION_VARIANTS + all admin form/editor chunks onto public routes. */
import { UNIVERSITY_ID } from "@/constants/admin/sectionTypes";
import { getTenantHeadersAsync } from "@/lib/tenantKey";
import { handleApiError } from "@/lib/utils";
import { isAssetPath } from "@/lib/assetPaths";
import { inferSource, serverLog } from "@/lib/serverLog";
import axios, { AxiosError } from "axios";

export const getPageData = async (
  slug1?: string,
  slug2?: string,
  slug3?: string
) => {
  try {
    const slugs = [slug1, slug2, slug3].filter(Boolean).join("/");

    // Asset probes (/favicon.ico, /apple-touch-icon.png, /manifest.json ...) are not CMS
    // pages. Without this they each cost a real API round-trip that 404s — and a 404 is
    // never cached, so it is paid on every single request. See lib/assetPaths.ts.
    if (isAssetPath(slugs)) {
      return null;
    }

    const url = `${APP_URL.API_BASE_URL}${FRONTEND_API_ENDPOINTS.PAGE(slugs)}`;

    const tenantHeaders = await getTenantHeadersAsync();

    /*
      `fetch`, NOT axios, and that is the whole point.

      Next's Data Cache works by monkey-patching the global `fetch`. Axios declares
      `adapter: ['xhr','http','fetch']` and picks the first supported one, so on the server it
      always uses Node's raw `http` module and never touches the patched global — every page
      read was therefore invisible to Next's caching machinery. This endpoint is the most
      expensive call in a render (~27 DB queries upstream), so it is the one worth caching.

      TENANT SAFETY. The cache key is a SHA-256 over [url, method, HEADERS, ...] — see
      next/dist/server/lib/incremental-cache/index.js `generateCacheKey`. `X-Tenant-Key` is in
      those headers, so two tenants requesting the same slug hash to different entries. That
      is what makes this safe where a route-level cache is not.

      DO NOT "improve" this by adding `export const revalidate` to a layout instead. The Full
      Route Cache keys on PATHNAME ONLY, and config/tenantByHost.json maps 48 hostnames onto
      23 tenants on one deployment — it would serve one tenant's page to another's visitors.
      See the note in app/(user)/(root)/layout.tsx. These routes stay dynamic SSR on purpose;
      the Data Cache is independent of that and works fine on a dynamic route.

      `tags` are unused until the CMS publish hook calls revalidateTag() — the CMS runs in the
      Express container and cannot reach this process, so that needs a route handler here.
      Until then a publish takes effect within `revalidate` seconds. Tagging now costs nothing
      and saves editing this function twice.
    */
    const startedAt = performance.now();
    const response = await fetch(url, {
      headers: tenantHeaders,
      next: {
        revalidate: 60,
        tags: [`page:${tenantHeaders["X-Tenant-Key"] ?? "default"}:${slugs || "home"}`],
      },
    });
    const ms = Math.round(performance.now() - startedAt);

    serverLog({
      evt: "cms_page_fetch",
      path: `/${slugs}`,
      tenant: tenantHeaders["X-Tenant-Key"] ?? null,
      status: response.status,
      ms,
      source: inferSource(ms),
    });

    // fetch does not throw on any HTTP status, so the old axios `validateStatus: () => true`
    // is the default here — missing pages are a plain 404, not an exception.
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const payload = await response.json();

    if (!payload?.success) {
      return null;
    }

    return { success: payload.success, data: payload.data };
  } catch (error) {
    console.error("Error fetching page data:", error);
    return null;
  }
};

export const getPageRoutes = async (noOfSlugs: number) => {
  const response = await axios.get(
    `${APP_URL?.API_BASE_URL + FRONTEND_API_ENDPOINTS.PAGE_ROUTES(noOfSlugs)}`,
    { headers: await getTenantHeadersAsync() }
  );

  return response?.status === 200 ? response.data : null;
};

export type BlogSitemapEntry = { path: string; lastModified?: Date };

/** All published blog page paths for SEO (paginates public `/page/blog-posts`). */
export async function getPublishedBlogSitemapEntries(): Promise<BlogSitemapEntry[]> {
  const apiBase = APP_URL.API_BASE_URL?.trim();
  if (!apiBase) return [];

  const limit = 50;
  const out: BlogSitemapEntry[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  try {
    while (offset < total) {
      const qs = new URLSearchParams({
        university_id: String(UNIVERSITY_ID),
        limit: String(limit),
        offset: String(offset),
        // Only blogs opted into the sitemap — honors the per-page include_in_sitemap
        // toggle (the blog-listing UI does not send this).
        sitemap_only: "true",
      });
      const url = `${apiBase}${FRONTEND_API_ENDPOINTS.BLOG_POSTS_PUBLIC}?${qs.toString()}`;
      const response = await axios.get(url, {
        validateStatus: () => true,
        headers: await getTenantHeadersAsync(),
      });
      if (response.status !== 200 || !response.data?.success || response.data?.data == null) {
        break;
      }

      const data = response.data.data as {
        items?: { path?: string; created_at?: string }[];
        total?: number;
      };
      const items = data.items ?? [];
      total = typeof data.total === "number" ? data.total : offset + items.length;

      for (const row of items) {
        const path = typeof row.path === "string" ? row.path.trim() : "";
        if (!path || path === "/") continue;
        out.push({
          path,
          lastModified: row.created_at ? new Date(row.created_at) : undefined,
        });
      }

      offset += items.length;
      if (items.length === 0) break;
    }
  } catch (err) {
    console.error("Error fetching blog URLs for sitemap:", err);
  }

  return out;
}

export async function getUniversitySettings() {
  try {
    const tenantHeaders = await getTenantHeadersAsync();
    const startedAt = performance.now();
    const response = await axios.get(
      `${APP_URL.API_BASE_URL}/university-settings/university/${UNIVERSITY_ID}/all`,
      { withCredentials: true, headers: tenantHeaders }
    );
    // Still axios, so this NEVER hits Next's Data Cache — source is always "origin".
    // That contrast against cms_page_fetch is the point: it shows what caching bought.
    serverLog({
      evt: "cms_settings_fetch",
      tenant: tenantHeaders["X-Tenant-Key"] ?? null,
      status: response.status,
      ms: Math.round(performance.now() - startedAt),
      source: "origin",
    });
    // APIResponse wraps data in { success: true, data: ... }
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/** Non-secret, DB-backed frontend config (chatbot ids, analytics ids, application_url). */
export type UniversityAppConfigPublic = {
  application_url: string | null;
  robots_disallow: string | null;
  chatbot_kenyt_bot_id: string | null;
  chatbot_tawk_property_id: string | null;
  chatbot_tawk_widget_id: string | null;
  chatbot_freshdesk_token: string | null;
  chatbot_freshdesk_host: string | null;
  chatbot_freshdesk_widget_uuid: string | null;
  chatbot_upgrad_client_key: string | null;
  chatbot_upgrad_bot_id: string | null;
  chatbot_upgrad_url: string | null;
  gtm_id: string | null;
  gtm_gateway_path: string | null;
  google_analytics_id: string | null;
  google_ads_code: string | null;
  google_ads_conversion_id: string | null;
  adsense_id: string | null;
  meta_pixel_id: string | null;
  microsoft_uet_tag_id: string | null;
  rudder_stack_source_write_key: string | null;
};

/**
 * Fetch the public (non-secret) app config for a university. Returns null on any
 * failure so callers can fall back to NEXT_PUBLIC_* env. Tenant-routed in
 * multi-tenant mode via getTenantHeadersAsync().
 */
export async function getUniversityAppConfigPublic(
  universityId: number = UNIVERSITY_ID
): Promise<UniversityAppConfigPublic | null> {
  try {
    const response = await axios.get(
      `${APP_URL.API_BASE_URL}/university-app-config/university/${universityId}/public`,
      { validateStatus: () => true, headers: await getTenantHeadersAsync() }
    );
    if (response.status !== 200 || !response.data?.success) return null;
    return response.data.data as UniversityAppConfigPublic;
  } catch (error) {
    console.error("Error fetching university app config:", error);
    return null;
  }
}

/** Raw llms.txt body configured for the university (empty string when unset/on error). */
export async function getUniversityLlmsTxt(
  universityId: number = UNIVERSITY_ID
): Promise<string> {
  try {
    const response = await axios.get(
      `${APP_URL.API_BASE_URL}/university-app-config/university/${universityId}/llms`,
      {
        validateStatus: () => true,
        responseType: "text",
        headers: await getTenantHeadersAsync(),
      }
    );
    return response.status === 200 && typeof response.data === "string" ? response.data : "";
  } catch (error) {
    console.error("Error fetching llms.txt:", error);
    return "";
  }
}

export async function getUniversityPublicInfo(): Promise<{
  name: string;
  favicon: string;
  default_sitemap_lastmod?: string | null;
  legal_name?: string;
  social_links?: string;
  address_street?: string;
  address_locality?: string;
  address_region?: string;
  address_postal_code?: string;
  address_country?: string;
  phone_link?: string;
} | null> {
  try {
    const tenantHeaders = await getTenantHeadersAsync();
    const startedAt = performance.now();
    const response = await axios.get(
      `${APP_URL.API_BASE_URL}/university/${UNIVERSITY_ID}/public`,
      { headers: tenantHeaders }
    );
    // Also still axios — see the note in getUniversitySettings.
    serverLog({
      evt: "cms_public_info_fetch",
      tenant: tenantHeaders["X-Tenant-Key"] ?? null,
      status: response.status,
      ms: Math.round(performance.now() - startedAt),
      source: "origin",
    });
    return response?.status === 200 ? response?.data?.data : null;
  } catch (error) {
    console.error("Error fetching university public info:", error);
    return null;
  }
}

export async function getUniversityName() {
  const info = await getUniversityPublicInfo();
  return info?.name ?? null;
}

/**
 * Global fallback `<lastmod>` for sitemap.xml entries
 * (university.default_sitemap_lastmod). Raw datetime string, or null when unset.
 */
export async function getSitemapDefaultLastmod(): Promise<string | null> {
  const info = await getUniversityPublicInfo();
  const v = info?.default_sitemap_lastmod;
  return v && String(v).trim() ? String(v).trim() : null;
}
